import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import MerchantTable from './components/MerchantTable';
import MerchantFilters from './components/MerchantFilters';
import StatsCard from './components/StatsCard';
import { merchantsService } from '../../services/supabaseService';

const MerchantListing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
    
    // Subscribe to real-time updates
    const unsubscribe = merchantsService?.subscribe(() => {
      fetchData();
    });

    return () => {
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [merchants, filters, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [merchantsResult, statsResult] = await Promise.all([
        merchantsService?.getAll(),
        merchantsService?.getStatistics()
      ]);

      if (merchantsResult?.data) {
        setMerchants(merchantsResult?.data);
      }

      if (statsResult?.data) {
        setStats(statsResult?.data);
      }
    } catch (error) {
      console.error('Error fetching merchant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...merchants];

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(merchant => merchant?.status === filters?.status);
    }

    // Apply business type filter
    if (filters?.businessType) {
      filtered = filtered?.filter(merchant => merchant?.businessType === filters?.businessType);
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(merchant => 
        merchant?.merchantName?.toLowerCase()?.includes(search) ||
        merchant?.email?.toLowerCase()?.includes(search) ||
        merchant?.merchantId?.toLowerCase()?.includes(search)
      );
    }

    setFilteredMerchants(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const handleViewDetails = (merchantId) => {
    navigate(`/merchants/${merchantId}`);
  };

  const handleViewTransactions = (merchantId) => {
    navigate(`/merchants/${merchantId}/transactions`);
  };

  const statsCards = [
    {
      title: 'Total Merchants',
      value: stats?.totalMerchants?.toString() || '0',
      icon: 'Users',
      iconColor: 'text-primary',
      iconBg: 'bg-primary/10',
      description: 'All registered merchants'
    },
    {
      title: 'Active Merchants',
      value: stats?.activeMerchants?.toString() || '0',
      icon: 'CheckCircle',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      description: 'Currently active'
    },
    {
      title: 'Avg Success Rate',
      value: `${stats?.avgSuccessRate?.toFixed(1) || 0}%`,
      icon: 'TrendingUp',
      iconColor: 'text-warning',
      iconBg: 'bg-warning/10',
      description: 'Transaction success'
    },
    {
      title: 'Avg Churn Risk',
      value: `${stats?.avgChurnRisk?.toFixed(1) || 0}%`,
      icon: 'AlertTriangle',
      iconColor: 'text-error',
      iconBg: 'bg-error/10',
      description: 'Merchants at risk'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Merchant Listing | PaymentNexus</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Merchants</h1>
            <p className="text-muted-foreground">Manage and monitor all registered merchants</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {statsCards?.map((stat, index) => (
              <StatsCard key={index} {...stat} loading={loading} />
            ))}
          </div>

          {/* Filters */}
          <MerchantFilters
            filters={filters}
            searchTerm={searchTerm}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onReset={handleResetFilters}
          />

          {/* Merchants Table */}
          <MerchantTable
            data={filteredMerchants}
            loading={loading}
            onViewDetails={handleViewDetails}
            onViewTransactions={handleViewTransactions}
          />
        </main>
      </div>
    </>
  );
};

export default MerchantListing;